import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import Button from '../../../common/Button';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useDispatch } from 'react-redux';
import {
  addAdditionalFee,
  updateAdditionalFeeList,
} from '../../../../redux/actions/basketActions';
import {
  addAdditionalFeeToLocalStorage,
  filterAdditionalFeeList,
} from '../helpers/basketHelpers';
import PropTypes from 'prop-types';
import { useEffectOnce } from 'react-use';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import Modal from 'react-modal';
import { customSort, parsePrice } from '../../../../utils/index';
import EmptyImageIcon from '../../../../assets/icons/emptyImageIcon';
import { checkIsSalesManagoEnabled } from '../../../../salesManago/helpers';
import { salesManagoUpdateCart } from '../../../../salesManago/events/shopCart';
import {
  getBasket,
  getPromoCode,
} from '../../../../redux/selectors/basketSelector';
import { getSelectedCity } from '../../../../redux/selectors/summaryReducer.selectors';
import { getServerConfig } from '../../../../features/general/generalSlice';
import { useAppSelector } from '../../../../config/hooks';
import { classNamesUtil } from '../../../../utils/classNamesUtil.util';

const ProductCarousel = ({ additionalFee }) => {
  const dispatch = useDispatch();
  const { code } = useAppSelector(getPromoCode) || {};
  const selectedCity = useAppSelector(getSelectedCity);
  const basket = useAppSelector(getBasket);
  const { companyId, salesManago, serverUrl } = useAppSelector(getServerConfig);

  const [swiper, setSwiper] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState({});

  const goNextSlide = () => {
    if (swiper !== null) {
      swiper.slideNext();
    }
  };

  const goPrevSlide = () => {
    if (swiper !== null) {
      swiper.slidePrev();
    }
  };

  const { availableFees, selectedFees } = additionalFee || {};

  useEffectOnce(() => {
    dispatch(
      updateAdditionalFeeList(
        'availableFees',
        filterAdditionalFeeList(availableFees, selectedFees)
      )
    );
  });

  const updateSalesManago = product => {
    if (checkIsSalesManagoEnabled(salesManago) && basket?.length) {
      const feesList = [...selectedFees];
      feesList.push(product);

      salesManagoUpdateCart(
        basket[0],
        code,
        selectedCity,
        feesList,
        serverUrl,
        salesManago,
        companyId
      ).catch(e => {
        console.log(e, 'e');
      });
    }
  };

  return (
    <>
      <Modal
        className="modal modal-additional"
        isOpen={isModalOpen}
        overlayClassName="overlay-form-fixed"
      >
        <div className="padding-all-24">
          <div
            className={classNamesUtil('modal-additional__image', {
              'modal-additional__image--empty': !activeProduct.imageUrl,
            })}
          >
            {activeProduct.imageUrl ? (
              <img alt="" src={activeProduct.imageUrl} />
            ) : (
              <EmptyImageIcon className="empty-image-icon" />
            )}
          </div>
          <h4 className="h400 spacer-bottom-8">{activeProduct?.name}</h4>
          <span className="label-l color-success">
            {parsePrice(activeProduct?.defaultPrice)} zł
          </span>
          {activeProduct?.description && (
            <div className="spacer-top-24">
              <span className="label-m color-gray-500 spacer-bottom-8 display-inline-block">
                Opis
              </span>
              <p className="body-l">{activeProduct?.description}</p>
            </div>
          )}
        </div>
        <div className="modal-additional__buttons">
          <Button
            colorClean
            label="Zamknij"
            onClick={() => {
              setIsModalOpen(false);
            }}
          />
          <Button
            label="Dodaj do zamówienia"
            onClick={() => {
              dispatch(
                addAdditionalFee({
                  ...activeProduct,
                  amount: activeProduct?.minQuantity || 1,
                })
              );
              addAdditionalFeeToLocalStorage({
                ...activeProduct,
                amount: activeProduct?.minQuantity || 1,
              });
              setIsModalOpen(false);

              updateSalesManago(activeProduct);
            }}
          />
        </div>
      </Modal>
      <section className="product-carousel">
        <div className="display-flex align-items-center justify-content-space-between spacer-bottom-16">
          <h3 className="label-l">
            Produkty, które możesz dodać do zamówienia
          </h3>
          <div className="product-carousel__buttons">
            <Button
              containerClass="spacer-left-32"
              label={<FontAwesomeIcon icon={faArrowLeft} />}
              onClick={() => goPrevSlide()}
              sizeMedium
            />
            <Button
              containerClass="spacer-left-4"
              label={<FontAwesomeIcon icon={faArrowRight} />}
              onClick={() => goNextSlide()}
              sizeMedium
            />
          </div>
        </div>
        <Swiper
          breakpoints={{
            0: {
              slidesPerView: 1.3,
            },
            450: {
              slidesPerView: 1.6,
            },
            560: {
              slidesPerView: 2.15,
            },
            768: {
              slidesPerView: 1.3,
            },
            900: {
              slidesPerView: 1.7,
            },
            1024: {
              slidesPerView: 2.3,
            },
            1250: {
              slidesPerView: 4,
            },
          }}
          lazy={true}
          longSwipesRatio={0.2}
          onSwiper={setSwiper}
          spaceBetween={16}
        >
          {customSort('name')(availableFees).map(product => {
            const { defaultPrice, imageUrl, name, possibleSideOrderId } =
              product;

            return (
              <SwiperSlide
                className="product-carousel__product"
                key={possibleSideOrderId}
              >
                <div>
                  <div
                    className={classNamesUtil('product-carousel__image', {
                      'product-carousel__image--empty': !imageUrl,
                    })}
                  >
                    {imageUrl ? (
                      <img alt="" src={imageUrl} />
                    ) : (
                      <EmptyImageIcon className="empty-image-icon" />
                    )}
                  </div>
                  <span className="product-carousel__info-icon">
                    <FontAwesomeIcon
                      className="font-size-20 "
                      icon={faInfoCircle}
                      onClick={() => {
                        setActiveProduct(product);
                        setIsModalOpen(true);
                      }}
                    />
                  </span>
                  <h6 className="label-m color-gray-800 spacer-bottom-4 display-flex align-items-center justify-content-space-between">
                    {name}
                  </h6>
                  <span className="body-s">{defaultPrice?.toFixed(2)} zł</span>
                </div>
                <Button
                  blockButton
                  colorClean
                  containerClass="color-primary"
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  label="Dodaj do zamówienia"
                  onClick={() => {
                    dispatch(
                      addAdditionalFee({
                        ...product,
                        amount: product?.minQuantity || 1,
                      })
                    );
                    addAdditionalFeeToLocalStorage({
                      ...product,
                      amount: product?.minQuantity || 1,
                    });
                    updateSalesManago(product);
                  }}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </section>
    </>
  );
};

ProductCarousel.propTypes = {
  additionalFee: PropTypes.object,
};

export default ProductCarousel;
